import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, FileText, ClipboardList, Users, Building2,
  UserCheck, Archive, Settings, LogOut, Sun, Moon, Menu, X, Shield, Calendar, Search, User, Receipt, BarChart3, Wrench
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { GlobalSearch } from './GlobalSearch';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/orders', label: 'Aufträge', icon: ClipboardList },
  { path: '/quotes', label: 'Offerten', icon: FileText },
  { path: '/contractors', label: 'Dienstleister', icon: Wrench },
  { path: '/customers', label: 'Verwaltungen', icon: Users },
  { path: '/owners', label: 'Eigentümer', icon: User },
  { path: '/properties', label: 'Liegenschaften', icon: Building2 },
  { path: '/renters', label: 'Mieter', icon: UserCheck },
  { path: '/calendar', label: 'Kalender', icon: Calendar },
  { path: '/finance', label: 'Übersicht', icon: BarChart3 },
  { path: '/invoices', label: 'Rechnungen', icon: Receipt },
  { path: '/archive', label: 'Archiv', icon: Archive },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-white/10 bg-[#5b243a]">
        <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          {tenant?.logo ? (
            <img src={tenant.logo} alt="Logo" className="h-9 w-auto object-contain rounded-sm max-w-[38px] bg-white/90 p-0.5" />
          ) : (
            <div className="w-9 h-9 bg-white/15 rounded-sm flex items-center justify-center">
              <Shield className="w-4 h-4 text-primary-foreground" />
            </div>
          )}
          <div>
            <p className="font-heading font-black text-base text-white tracking-tight leading-tight">{tenant?.company_name || 'Your Hero'}</p>
            <p className="text-xs text-white/70 truncate max-w-[120px]">{user?.name || ''}</p>
          </div>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto bg-[#f7f1f4]">
        {/* Global Search */}
        <div className="mb-2">
          <GlobalSearch />
        </div>
        {navItems.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));
          return (
            <Link
              key={path}
              to={path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${label.toLowerCase()}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-sm text-base font-semibold transition-colors",
                active
                  ? "bg-[#5b243a] text-white"
                  : "text-[#4b2033]/75 hover:text-[#4b2033] hover:bg-white"
              )}
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={active ? 2 : 1.5} />
              {label}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="px-3 py-4 border-t border-white/10 space-y-1 bg-[#4b2033]">
        <Link
          to="/profile"
          onClick={() => setSidebarOpen(false)}
          className={cn("flex items-center gap-3 px-3 py-2.5 rounded-sm text-base font-semibold transition-colors",
            location.pathname === '/profile' ? "bg-white text-[#4b2033]" : "text-white/75 hover:text-white hover:bg-white/10"
          )}
        >
          <User className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          Mein Konto
        </Link>
        {user?.role === 'admin' && (
          <Link
            to="/admin"
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-admin"
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-sm text-base font-semibold transition-colors",
              location.pathname === '/admin'
                ? "bg-white text-[#4b2033]"
                : "text-white/75 hover:text-white hover:bg-white/10"
            )}
          >
            <Settings className="w-4 h-4 shrink-0" strokeWidth={1.5} />
            Einstellungen
          </Link>
        )}
        <button
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          data-testid="theme-toggle"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-base font-semibold text-white/75 hover:text-white hover:bg-white/10 transition-colors"
        >
          {theme === 'dark' ? <Sun className="w-4 h-4 shrink-0" strokeWidth={1.5} /> : <Moon className="w-4 h-4 shrink-0" strokeWidth={1.5} />}
          {theme === 'dark' ? 'Hell' : 'Dunkel'}
        </button>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-base font-semibold text-white/75 hover:text-white hover:bg-white/10 transition-colors"
        >
          <LogOut className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          Abmelden
        </button>
        <div className="px-3 py-2">
          <p className="text-xs font-medium text-white truncate">{user?.name}</p>
          <p className="text-xs text-white/60 truncate">{user?.email}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-56 shrink-0 flex-col border-r border-[#5b243a]/20 bg-[#f7f1f4]">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-56 h-full bg-card border-r border-border shadow-xl">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-border/60 bg-card/80 backdrop-blur-sm">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5 text-muted-foreground" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Shield className="w-4 h-4 text-primary" />
            <span className="font-heading font-black text-sm">Your Hero</span>
          </Link>
          <div className="w-5" />
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
